import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '../config';

const inputStyle = {
  background: 'rgba(0,0,0,0.2)',
  border: '1px solid rgba(255,255,255,0.1)',
  color: '#F8FAF9',
};

/**
 * Province / Regency / District structured dropdowns for Indonesia.
 * Emits the *name* strings (not IDs) via onChange so payloads stay human-readable.
 *
 * Props:
 *   value: { province, city, district }
 *   onChange(next): called whenever any field changes
 */
export const RegionSelect = ({ value, onChange, required = false }) => {
  const [provinces, setProvinces] = useState([]);
  const [regencies, setRegencies] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [provinceId, setProvinceId] = useState('');
  const [regencyId, setRegencyId] = useState('');

  useEffect(() => {
    axios.get(`${API}/regions/provinces`)
      .then((r) => setProvinces(r.data || []))
      .catch(() => setProvinces([]));
  }, []);

  // Sync provinceId from value.province on mount / when prefilled
  useEffect(() => {
    if (!value.province || provinces.length === 0) return;
    const match = provinces.find((p) => p.name === value.province);
    if (match && match.id !== provinceId) setProvinceId(match.id);
  }, [value.province, provinces, provinceId]);

  useEffect(() => {
    if (!provinceId) { setRegencies([]); return; }
    axios.get(`${API}/regions/regencies`, { params: { province_id: provinceId } })
      .then((r) => setRegencies(r.data || []))
      .catch(() => setRegencies([]));
  }, [provinceId]);

  useEffect(() => {
    if (!value.city || regencies.length === 0) return;
    const match = regencies.find((r) => r.name === value.city);
    if (match && match.id !== regencyId) setRegencyId(match.id);
  }, [value.city, regencies, regencyId]);

  useEffect(() => {
    if (!regencyId) { setDistricts([]); return; }
    axios.get(`${API}/regions/districts`, { params: { regency_id: regencyId } })
      .then((r) => setDistricts(r.data || []))
      .catch(() => setDistricts([]));
  }, [regencyId]);

  const handleProvince = (e) => {
    const id = e.target.value;
    setProvinceId(id);
    setRegencyId('');
    const prov = provinces.find((p) => p.id === id);
    onChange({ province: prov ? prov.name : '', city: '', district: '' });
  };

  const handleRegency = (e) => {
    const id = e.target.value;
    setRegencyId(id);
    const reg = regencies.find((r) => r.id === id);
    onChange({ ...value, city: reg ? reg.name : '', district: '' });
  };

  const handleDistrict = (e) => {
    onChange({ ...value, district: e.target.value });
  };

  return (
    <div className="space-y-3" data-testid="region-select">
      <select
        value={provinceId}
        onChange={handleProvince}
        required={required}
        className="w-full rounded-lg px-3 py-2.5 outline-none"
        style={inputStyle}
        data-testid="region-province-select"
      >
        <option value="">Pilih Provinsi</option>
        {provinces.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
      </select>
      <select
        value={regencyId}
        onChange={handleRegency}
        required={required}
        disabled={!provinceId}
        className="w-full rounded-lg px-3 py-2.5 outline-none disabled:opacity-50"
        style={inputStyle}
        data-testid="region-regency-select"
      >
        <option value="">Pilih Kota / Kabupaten</option>
        {regencies.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
      </select>
      <select
        value={value.district || ''}
        onChange={handleDistrict}
        required={required}
        disabled={!regencyId}
        className="w-full rounded-lg px-3 py-2.5 outline-none disabled:opacity-50"
        style={inputStyle}
        data-testid="region-district-select"
      >
        <option value="">Pilih Kecamatan</option>
        {districts.map((d) => <option key={d.id} value={d.name}>{d.name}</option>)}
      </select>
    </div>
  );
};
